import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { FiPrinter, FiSearch, FiCheckSquare, FiSquare, FiAlertCircle, FiX } from 'react-icons/fi';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { colors, borderRadius, shadows, typography, spacing } from '../../theme/colors';
import useCurrency from '../../hooks/useCurrency';
import { printBarcodeLabels, DEFAULT_LABEL } from '../../utils/barcodeLabelPrint';

/**
 * Bulk barcode-label print page.
 *
 * Lists active inventory items, lets the cashier set per-item quantities,
 * then prints labels through the printer-safe helper. Settings come from
 * Print Formats → Barcode Labels (or DEFAULT_LABEL if not configured yet).
 *
 * Module-gated: only reachable when `master_barcode_labels` is enabled.
 */
const BarcodeLabels = () => {
  const { activeCompany, isModuleEnabled } = useAuth();
  const { formatCurrency } = useCurrency();
  const companyId = activeCompany?.id;
  const moduleOn = !!(isModuleEnabled && isModuleEnabled('master_barcode_labels'));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [filterSubCategoryId, setFilterSubCategoryId] = useState('');
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  // Map<itemId, qty> — only items with a positive qty get printed.
  const [qtyByItem, setQtyByItem] = useState({});
  const [labelSettings, setLabelSettings] = useState(null);

  useEffect(() => {
    if (!companyId || !moduleOn) return;
    (async () => {
      setLoading(true);
      try {
        const [itemsSnap, catSnap, subCatSnap, fmtSnap] = await Promise.all([
          getDocs(collection(db, 'companies', companyId, 'inventoryItems')),
          getDocs(collection(db, 'companies', companyId, 'inventoryCategories')),
          getDocs(collection(db, 'companies', companyId, 'inventorySubCategories')),
          getDoc(doc(db, 'companies', companyId, 'settings', 'documentFormats')).catch(() => null)
        ]);
        setItems(
          itemsSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(i => i.isActive !== false && i.isDeleted !== true)
        );
        setCategories(catSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => c.isDeleted !== true));
        setSubCategories(subCatSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.isDeleted !== true));
        const fmt = fmtSnap && fmtSnap.exists?.() ? (fmtSnap.data()?.barcodeLabel || null) : null;
        setLabelSettings(fmt);
      } catch (err) {
        console.error('Barcode labels load failed:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId, moduleOn]);

  // Subcategories shown in the filter — those under the selected category, or
  // all of them when no category is picked.
  const visibleSubCategories = useMemo(() =>
    filterCategoryId ? subCategories.filter(s => s.categoryId === filterCategoryId) : subCategories,
  [subCategories, filterCategoryId]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(it => {
      if (filterCategoryId && it.categoryId !== filterCategoryId) return false;
      if (filterSubCategoryId && it.subCategoryId !== filterSubCategoryId) return false;
      if (!q) return true;
      return (it.itemName || '').toLowerCase().includes(q)
        || (it.itemCode || '').toLowerCase().includes(q)
        || (it.barcode || '').toLowerCase().includes(q);
    });
  }, [items, search, filterCategoryId, filterSubCategoryId]);

  const totalLabels = useMemo(() =>
    Object.values(qtyByItem).reduce((s, q) => s + (Number(q) || 0), 0),
  [qtyByItem]);

  const selectedCount = useMemo(() =>
    Object.values(qtyByItem).filter(q => Number(q) > 0).length,
  [qtyByItem]);

  const setQty = (itemId, qty) => {
    const n = Math.max(0, Math.floor(Number(qty) || 0));
    setQtyByItem(prev => {
      const next = { ...prev };
      if (n === 0) delete next[itemId];
      else next[itemId] = n;
      return next;
    });
  };

  const toggleItem = (itemId) => {
    setQtyByItem(prev => {
      const next = { ...prev };
      if (next[itemId]) delete next[itemId];
      else next[itemId] = 1;
      return next;
    });
  };

  const bulkSetQty = (q) => {
    // Apply qty to all currently filtered items that have a barcode
    setQtyByItem(prev => {
      const next = { ...prev };
      filteredItems.forEach(it => {
        if (q > 0 && it.barcode) next[it.id] = q;
        else delete next[it.id];
      });
      return next;
    });
  };

  const clearAll = () => setQtyByItem({});

  const handlePrint = () => {
    const rows = items
      .filter(it => Number(qtyByItem[it.id]) > 0)
      .map(it => ({ item: it, qty: Number(qtyByItem[it.id]) }));
    const missingBarcode = rows.filter(r => !r.item.barcode);
    if (missingBarcode.length > 0) {
      const names = missingBarcode.slice(0, 5).map(r => `· ${r.item.itemName || r.item.itemCode}`).join('\n');
      const more = missingBarcode.length > 5 ? `\n…and ${missingBarcode.length - 5} more` : '';
      const proceed = window.confirm(
        `${missingBarcode.length} selected item(s) have no barcode — they'll be skipped:\n\n${names}${more}\n\nProceed?`
      );
      if (!proceed) return;
    }
    const printable = rows.filter(r => r.item.barcode);
    if (printable.length === 0) {
      alert('Nothing to print — set a quantity for at least one item that has a barcode.');
      return;
    }
    printBarcodeLabels(printable, activeCompany, labelSettings || DEFAULT_LABEL);
  };

  if (!moduleOn) {
    return (
      <PageLayout pageTitle="Barcode Labels">
        <DisabledNotice>
          <FiAlertCircle size={20} />
          <div>
            <strong>Barcode Labels is disabled for this company.</strong>
            <p>Ask your Super Admin to turn on the <code>Barcode Labels</code> module from <em>SuperAdmin → Manage Modules</em>.</p>
          </div>
        </DisabledNotice>
      </PageLayout>
    );
  }

  return (
    <PageLayout pageTitle="Barcode Labels">
      <Container>
        <HeaderRow>
          <div>
            <PageTitle>Barcode Labels</PageTitle>
            <PageSub>
              Bulk print labels to Zebra / TSC thermal printers. Layout is configured in
              <strong> Settings → Print Formats → Barcode Labels</strong>.
            </PageSub>
          </div>
          <PrintBtn onClick={handlePrint} disabled={totalLabels === 0}>
            <FiPrinter size={18} />
            PRINT {totalLabels > 0 ? `${totalLabels} LABEL${totalLabels > 1 ? 'S' : ''}` : ''}
          </PrintBtn>
        </HeaderRow>

        <Toolbar>
          <SearchBox>
            <FiSearch size={16} />
            <SearchInput
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, code, or barcode…"
            />
            {search && <ClearBtn onClick={() => setSearch('')}><FiX size={14} /></ClearBtn>}
          </SearchBox>
          <Select value={filterCategoryId} onChange={e => { setFilterCategoryId(e.target.value); setFilterSubCategoryId(''); }}>
            <option value="">All categories</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.categoryName || c.name}</option>
            ))}
          </Select>
          <Select value={filterSubCategoryId} onChange={e => setFilterSubCategoryId(e.target.value)}>
            <option value="">All subcategories</option>
            {visibleSubCategories.map(s => (
              <option key={s.id} value={s.id}>{s.subCategoryName || s.name}</option>
            ))}
          </Select>
          <BulkChip onClick={() => bulkSetQty(1)} title="Set 1 label for every filtered item">+1 each</BulkChip>
          <BulkChip onClick={() => bulkSetQty(5)} title="Set 5 labels for every filtered item">+5 each</BulkChip>
          <BulkChip onClick={() => bulkSetQty(10)}>+10 each</BulkChip>
          <BulkChip onClick={clearAll} $danger>Clear all</BulkChip>
        </Toolbar>

        <SummaryBar>
          <span><strong>{selectedCount}</strong> item{selectedCount !== 1 ? 's' : ''} selected</span>
          <span>·</span>
          <span><strong>{totalLabels}</strong> total label{totalLabels !== 1 ? 's' : ''}</span>
          <span>·</span>
          <span>{filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''} in view</span>
        </SummaryBar>

        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th style={{ width: 36 }}></th>
                <th>Item</th>
                <th style={{ width: 140 }}>Code</th>
                <th style={{ width: 160 }}>Barcode</th>
                <th style={{ width: 120, textAlign: 'right' }}>Sale Price</th>
                <th style={{ width: 120, textAlign: 'center' }}>Qty</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><Td colSpan={6} style={{ textAlign: 'center', padding: 40 }}>Loading…</Td></tr>
              ) : filteredItems.length === 0 ? (
                <tr><Td colSpan={6} style={{ textAlign: 'center', padding: 40, color: colors.text.tertiary }}>No items match this filter.</Td></tr>
              ) : filteredItems.map(it => {
                const qty = Number(qtyByItem[it.id]) || 0;
                const noBc = !it.barcode;
                return (
                  <Row key={it.id} $selected={qty > 0} $warn={noBc}>
                    <Td>
                      <CheckBtn onClick={() => toggleItem(it.id)} title={noBc ? 'Item has no barcode' : ''}>
                        {qty > 0 ? <FiCheckSquare size={18} /> : <FiSquare size={18} />}
                      </CheckBtn>
                    </Td>
                    <Td>
                      <ItemName>{it.itemName || '(unnamed)'}</ItemName>
                      {it.description && <ItemDesc>{it.description}</ItemDesc>}
                    </Td>
                    <Td><Mono>{it.itemCode || '—'}</Mono></Td>
                    <Td>
                      {it.barcode ? (
                        <Mono>{it.barcode}</Mono>
                      ) : (
                        <NoBcTag><FiAlertCircle size={11} /> no barcode</NoBcTag>
                      )}
                    </Td>
                    <Td style={{ textAlign: 'right' }}>{formatCurrency(it.salePrice || 0)}</Td>
                    <Td style={{ textAlign: 'center' }}>
                      <QtyInput
                        type="number"
                        min={0}
                        max={9999}
                        value={qty || ''}
                        placeholder="0"
                        onChange={e => setQty(it.id, e.target.value)}
                        disabled={noBc}
                      />
                    </Td>
                  </Row>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>

        <PrinterTips>
          <strong>Printer tips for Zebra / TSC:</strong>
          <ol>
            <li>Set the driver paper size to your label dimensions <em>before</em> opening the print dialog.</li>
            <li>In the browser print dialog: <strong>Scale = 100%</strong> (NOT "Fit to page"), <strong>Margins = None</strong>.</li>
            <li>If labels print blank: increase the barcode-image contrast (Zebra Setup Utility → Darkness).</li>
            <li>If the printer feeds extra blank labels at the end of a run, recalibrate the gap sensor.</li>
          </ol>
        </PrinterTips>
      </Container>
    </PageLayout>
  );
};

// ─── Styled ──────────────────────────────────────────────────────────────

const Container = styled.div` max-width: 1200px; margin: 0 auto; `;
const HeaderRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 16px;
`;
const PageTitle = styled.h1` margin: 0 0 4px; font-size: 22px; color: ${colors.text.primary}; `;
const PageSub = styled.p` margin: 0; font-size: 13px; color: ${colors.text.secondary}; max-width: 740px; `;
const PrintBtn = styled.button`
  display: inline-flex; align-items: center; gap: 8px;
  padding: 12px 22px;
  background: ${colors.primary.main};
  color: white;
  border: none; border-radius: ${borderRadius.md};
  font-size: 14px; font-weight: 700; letter-spacing: 0.5px;
  cursor: pointer;
  &:hover:not(:disabled) { filter: brightness(0.92); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
const Toolbar = styled.div`
  display: flex; gap: 8px; align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
`;
const SearchBox = styled.div`
  flex: 1;
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px;
  background: white;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  color: ${colors.text.secondary};
  min-width: 240px;
  &:focus-within { border-color: ${colors.primary.main}; }
`;
const SearchInput = styled.input`
  flex: 1; border: none; outline: none; font-size: 14px; background: transparent;
`;
const ClearBtn = styled.button`
  background: none; border: none; color: ${colors.text.tertiary}; cursor: pointer; padding: 2px;
`;
const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  background: white;
  font-size: 14px;
`;
const BulkChip = styled.button`
  padding: 8px 14px;
  border-radius: 16px;
  border: 1px solid ${({ $danger }) => $danger ? colors.error.main : colors.border.light};
  background: ${({ $danger }) => $danger ? colors.error.main + '10' : 'white'};
  color: ${({ $danger }) => $danger ? colors.error.main : colors.text.primary};
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  &:hover { background: ${({ $danger }) => $danger ? colors.error.main + '20' : colors.neutral[100]}; }
`;
const SummaryBar = styled.div`
  display: flex; gap: 10px; align-items: center;
  padding: 8px 12px;
  background: ${colors.neutral[50]};
  border-radius: ${borderRadius.sm};
  margin-bottom: 8px;
  font-size: 13px;
  color: ${colors.text.secondary};
`;
const TableWrap = styled.div`
  background: white;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  box-shadow: ${shadows.sm};
  overflow: hidden;
  margin-bottom: 16px;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  thead th {
    text-align: left;
    padding: 10px 12px;
    background: ${colors.neutral[50]};
    border-bottom: 1px solid ${colors.border.light};
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${colors.text.secondary};
    font-weight: 600;
  }
`;
const Row = styled.tr`
  background: ${({ $selected, $warn }) =>
    $selected ? colors.primary.main + '10'
    : $warn ? '#FFF8E1'
    : 'transparent'};
  border-bottom: 1px solid ${colors.neutral[100]};
  &:hover { background: ${({ $selected }) => $selected ? colors.primary.main + '18' : colors.neutral[50]}; }
`;
const Td = styled.td` padding: 10px 12px; vertical-align: middle; `;
const ItemName = styled.div` font-weight: 600; color: ${colors.text.primary}; `;
const ItemDesc = styled.div` font-size: 11px; color: ${colors.text.tertiary}; margin-top: 2px; `;
const Mono = styled.span` font-family: ${typography.fontFamily.mono}; font-size: 12px; color: ${colors.text.secondary}; `;
const NoBcTag = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; color: ${colors.warning.main};
  font-weight: 600;
`;
const CheckBtn = styled.button`
  background: none; border: none; cursor: pointer;
  color: ${colors.primary.main}; padding: 4px;
  display: flex;
`;
const QtyInput = styled.input`
  width: 80px;
  text-align: center;
  padding: 6px 8px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.sm};
  font-size: 14px;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
  &:disabled { background: ${colors.neutral[100]}; color: ${colors.text.tertiary}; cursor: not-allowed; }
`;
const PrinterTips = styled.div`
  padding: 12px 16px;
  background: #FFF3CD;
  color: #5B4400;
  border: 1px solid #FCE588;
  border-radius: ${borderRadius.md};
  font-size: 12px;
  line-height: 1.5;
  strong { color: #B45309; }
  ol { margin: 6px 0 0 16px; padding: 0; }
  li { margin-bottom: 3px; }
`;
const DisabledNotice = styled.div`
  display: flex; gap: 12px;
  padding: 20px;
  background: #FEF3C7;
  color: #92400E;
  border: 1px solid #FBBF24;
  border-radius: ${borderRadius.md};
  strong { display: block; margin-bottom: 4px; }
  p { margin: 0; font-size: 13px; }
  code { background: #FBBF2440; padding: 1px 5px; border-radius: 3px; }
`;

export default BarcodeLabels;
